import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Users, GripVertical, FolderOpen, Lock, Unlock, Calendar, FlaskConical, AlertTriangle, Loader2, Shield, Key, History, Circle, Eye, Wallet, Building2, CheckCircle, Settings2, RefreshCw, Send, Clock, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import UserAccess from './UserAccess';
import UserManagement from './settings/UserManagement';
import TestDataManager from './settings/TestDataManager';
import { versionService, clearCacheAndReload } from '@/lib/versionService';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

const BANK_ACCOUNTS = ["592", "OD", "Z-Del", "Z-Dine", "S-Del", "S-Dine", "Easy-Din", "G-Rabit", "Loan"];

// Sortable Category Item Component
const SortableCategoryItem = ({ category, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="border rounded-lg p-3 bg-white hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          <button {...attributes} {...listeners} className="cursor-grab hover:bg-gray-100 p-1 rounded">
            <GripVertical className="w-4 h-4 text-gray-400" />
          </button>
          <div>
            <p className="font-medium text-sm">{category.name}</p>
            {!category.is_active && (
              <Badge variant="outline" className="text-xs mt-1">Inactive</Badge>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(category.id, category.name)}
          data-testid={`delete-category-${category.id}`}
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
};

// Sortable Group Item Component
const SortableGroupItem = ({ group, onDelete, categoryCount }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <button {...attributes} {...listeners} className="cursor-grab hover:bg-gray-100 p-1 rounded">
            <GripVertical className="w-4 h-4 text-gray-400" />
          </button>
          <FolderOpen className="w-5 h-5 text-blue-500" />
          <div>
            <p className="font-medium">{group.name}</p>
            <p className="text-xs text-gray-500">{categoryCount} categories</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(group.id, group.name)}
          disabled={categoryCount > 0}
          title={categoryCount > 0 ? "Remove all categories first" : "Delete group"}
          data-testid={`delete-group-${group.id}`}
        >
          <Trash2 className={`w-4 h-4 ${categoryCount > 0 ? 'text-gray-300' : 'text-red-500'}`} />
        </Button>
      </div>
    </div>
  );
};

const Settings = ({ user }) => {
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [frozenMonths, setFrozenMonths] = useState([]);
  const [testDataStats, setTestDataStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearingTestData, setClearingTestData] = useState(false);
  const [factoryResetting, setFactoryResetting] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [groupDialog, setGroupDialog] = useState(false);
  const [freezeDialog, setFreezeDialog] = useState(false);
  const [userDialog, setUserDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteGroupDialog, setDeleteGroupDialog] = useState(false);
  const [deleteUserDialog, setDeleteUserDialog] = useState(false);
  const [clearTestDataDialog, setClearTestDataDialog] = useState(false);
  const [factoryResetDialog, setFactoryResetDialog] = useState(false);
  const [factoryResetConfirm, setFactoryResetConfirm] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '' });
  const [monthToFreeze, setMonthToFreeze] = useState('');
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'cashier',
    outlet: ''
  });
  const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [logsDialog, setLogsDialog] = useState({ open: false, user: null, sessions: [], activities: [] });
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Initial Setup state
  const [initialSetupStatus, setInitialSetupStatus] = useState({ cash: false, bank: false, loading: true });
  const [setupDate, setSetupDate] = useState('2026-01-01');
  const [openingCash, setOpeningCash] = useState('');
  const [bankBalances, setBankBalances] = useState({});
  const [savingSetup, setSavingSetup] = useState(false);
  const [editBankDialog, setEditBankDialog] = useState({ open: false, account: '', amount: '' });
  const [savingBankEdit, setSavingBankEdit] = useState(false);
  
  // Loading states to prevent double-click
  const [addingUser, setAddingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);
  const [addingGroup, setAddingGroup] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [freezingMonth, setFreezingMonth] = useState(false);
  const [unfreezingMonth, setUnfreezingMonth] = useState(false);
  
  // Database Backup state
  const [backupPassword, setBackupPassword] = useState('');
  const [backupConfirmPassword, setBackupConfirmPassword] = useState('');
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPassword, setImportPassword] = useState('');
  const [importConfirmPhrase, setImportConfirmPhrase] = useState('');
  const [importing, setImporting] = useState(false);
  const [autoBackups, setAutoBackups] = useState([]);
  const [loadingAutoBackups, setLoadingAutoBackups] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState({ open: false, filename: '' });
  const [restoreConfirmPhrase, setRestoreConfirmPhrase] = useState('');
  const [restoring, setRestoring] = useState(false);
  
  // Telegram Settings state
  const [telegramConfig, setTelegramConfig] = useState({
    chat_ids: [],
    schedule_time: '04:00',
    enabled: true,
    report_type: 'cashflow_pdf'
  });
  const [telegramStatus, setTelegramStatus] = useState({ configured: false, bot_info: null });
  const [loadingTelegram, setLoadingTelegram] = useState(false);
  const [savingTelegram, setSavingTelegram] = useState(false);
  const [newChatId, setNewChatId] = useState('');
  const [testingTelegram, setTestingTelegram] = useState(false);
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    group: '',
    is_active: true
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/expense-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch categories');
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/deduction-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups');
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchFrozenMonths = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/monthly-freezes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFrozenMonths(response.data);
    } catch (error) {
      console.error('Failed to fetch frozen months');
    }
  };

  // Telegram Settings functions
  const fetchTelegramSettings = async () => {
    setLoadingTelegram(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch Telegram status
      const statusRes = await axios.get(`${API}/ceo-report/telegram-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTelegramStatus({
        configured: statusRes.data.configured,
        bot_info: statusRes.data.bot_info
      });
      
      // Fetch schedule config
      const scheduleRes = await axios.get(`${API}/ceo-report/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTelegramConfig({
        chat_ids: scheduleRes.data.chat_ids || [],
        schedule_time: scheduleRes.data.schedule_time || '04:00',
        enabled: scheduleRes.data.enabled !== false,
        report_type: scheduleRes.data.report_type || 'cashflow_pdf'
      });
    } catch (error) {
      console.error('Failed to fetch Telegram settings:', error);
    } finally {
      setLoadingTelegram(false);
    }
  };

  const saveTelegramSettings = async () => {
    setSavingTelegram(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/ceo-report/schedule`, {
        chat_ids: telegramConfig.chat_ids,
        schedule_time: telegramConfig.schedule_time,
        enabled: telegramConfig.enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Telegram settings saved successfully!');
    } catch (error) {
      toast.error(`Failed to save Telegram settings: ${getErrorMessage(error)}`);
    } finally {
      setSavingTelegram(false);
    }
  };

  const addChatId = () => {
    if (!newChatId.trim()) {
      toast.error('Please enter a Chat ID');
      return;
    }
    if (telegramConfig.chat_ids.includes(newChatId.trim())) {
      toast.error('This Chat ID is already added');
      return;
    }
    setTelegramConfig(prev => ({
      ...prev,
      chat_ids: [...prev.chat_ids, newChatId.trim()]
    }));
    setNewChatId('');
    toast.success('Chat ID added. Click Save to apply changes.');
  };

  const removeChatId = (idToRemove) => {
    setTelegramConfig(prev => ({
      ...prev,
      chat_ids: prev.chat_ids.filter(id => id !== idToRemove)
    }));
    toast.info('Chat ID removed. Click Save to apply changes.');
  };

  const testTelegramReport = async () => {
    setTestingTelegram(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/cash-flow/send-telegram?period=this_month`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(`Test report sent to ${response.data.success_count} recipient(s)!`);
      } else {
        toast.error('Failed to send test report');
      }
    } catch (error) {
      toast.error(`Failed to send test report: ${getErrorMessage(error)}`);
    } finally {
      setTestingTelegram(false);
    }
  };

  const fetchInitialSetupStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const [cashRes, bankRes] = await Promise.all([
        axios.get(`${API}/dayclosings/initial-setup-status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bank-opening-balances-status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setInitialSetupStatus({
        cash: !cashRes.data.needs_setup,
        bank: !bankRes.data.needs_setup,
        loading: false,
        cashConfig: cashRes.data.initial_config,
        bankBalances: bankRes.data.opening_balances  // Use opening_balances, not current_balances
      });
      
      if (cashRes.data.initial_config) {
        setOpeningCash(cashRes.data.initial_config.opening_cash?.toString() || '');
        setSetupDate(cashRes.data.initial_config.setup_date || '2026-01-01');
      }
    } catch (error) {
      console.error('Failed to fetch initial setup status');
      setInitialSetupStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSaveOpeningCash = async () => {
    if (!openingCash || parseFloat(openingCash) < 0) {
      toast.error('Please enter a valid opening cash amount');
      return;
    }
    
    setSavingSetup(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/dayclosings/initial-opening-cash`, {
        date: setupDate,
        opening_cash: parseFloat(openingCash)
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Opening cash saved successfully!');
      fetchInitialSetupStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save opening cash'));
    } finally {
      setSavingSetup(false);
    }
  };

  const handleSaveBankBalances = async () => {
    const validBalances = Object.entries(bankBalances)
      .filter(([_, val]) => val && parseFloat(val) > 0)
      .reduce((acc, [key, val]) => ({ ...acc, [key]: parseFloat(val) }), {});
    
    if (Object.keys(validBalances).length === 0) {
      toast.error('Please enter at least one bank balance');
      return;
    }
    
    setSavingSetup(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bank-opening-balances`, {
        date: setupDate,
        balances: validBalances
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Bank opening balances saved successfully!');
      fetchInitialSetupStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save bank balances'));
    } finally {
      setSavingSetup(false);
    }
  };

  const handleEditBankBalance = async () => {
    const { account, amount } = editBankDialog;
    const numAmount = parseFloat(amount);
    
    if (isNaN(numAmount) || numAmount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSavingBankEdit(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/bank-opening-balances/${account}`, {
        amount: numAmount
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Opening balance for ${account} updated successfully!`);
      setEditBankDialog({ open: false, account: '', amount: '' });
      fetchInitialSetupStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update bank balance'));
    } finally {
      setSavingBankEdit(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchGroups();
    fetchUsers();
    fetchFrozenMonths();
    fetchTestDataStats();
    fetchInitialSetupStatus();
    fetchTelegramSettings();
  }, []);

  const fetchTestDataStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/test-data/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestDataStats(response.data);
    } catch (error) {
      console.error('Failed to fetch test data stats');
    }
  };

  const handleClearTestData = async () => {
    setClearingTestData(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/test-data/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setClearTestDataDialog(false);
      fetchTestDataStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to clear test data'));
    } finally {
      setClearingTestData(false);
    }
  };

  const handleFactoryReset = async () => {
    if (factoryResetConfirm !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }
    setFactoryResetting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/test-data/factory-reset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message + ' - Reloading app...');
      setFactoryResetDialog(false);
      setFactoryResetConfirm('');
      // Reload the entire app to refresh all cached data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to perform factory reset'));
      setFactoryResetting(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (addingUser) return; // Prevent double-click
    
    try {
      setAddingUser(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/register`, userForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User "${userForm.name}" created successfully!`);
      setUserDialog(false);
      setUserForm({ name: '', email: '', password: '', phone: '', role: 'cashier', outlet: '' });
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (deletingUser) return; // Prevent double-click
    
    try {
      setDeletingUser(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      setDeleteUserDialog(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    } finally {
      setDeletingUser(false);
    }
  };

  const openDeleteUserDialog = (userId, userName) => {
    setUserToDelete({ id: userId, name: userName });
    setDeleteUserDialog(true);
  };

  const handleChangePassword = async () => {
    if (changingPassword) return; // Prevent double-click
    if (!passwordDialog.user || !newPassword) return;
    if (newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    
    try {
      setChangingPassword(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/${passwordDialog.user.id}/password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Password changed for ${passwordDialog.user.name}`);
      setPasswordDialog({ open: false, user: null });
      setNewPassword('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to change password'));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleViewLogs = async (userItem) => {
    setLogsDialog({ open: true, user: userItem, sessions: [], activities: [] });
    setLoadingLogs(true);
    
    try {
      const token = localStorage.getItem('token');
      const [sessionsRes, activityRes] = await Promise.all([
        axios.get(`${API}/users/${userItem.id}/sessions?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/users/${userItem.id}/activity?limit=30`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setLogsDialog({
        open: true,
        user: userItem,
        sessions: sessionsRes.data.sessions || [],
        activities: activityRes.data.activities || []
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to fetch user logs'));
    } finally {
      setLoadingLogs(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (addingCategory) return; // Prevent double-click
    if (!categoryForm.group) {
      toast.error('Please select a group');
      return;
    }
    try {
      setAddingCategory(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expense-categories`, categoryForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category added!');
      setCategoryDialog(false);
      setCategoryForm({ name: '', group: '', is_active: true });
      fetchCategories();
    } catch (error) {
      toast.error('Failed to add category');
    } finally {
      setAddingCategory(false);
    }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault();
    if (addingGroup) return; // Prevent double-click
    
    try {
      setAddingGroup(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/deduction-groups`, groupForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Group added!');
      setGroupDialog(false);
      setGroupForm({ name: '' });
      fetchGroups();
    } catch (error) {
      toast.error('Failed to add group');
    } finally {
      setAddingGroup(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (deletingCategory) return; // Prevent double-click
    
    try {
      setDeletingCategory(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/expense-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category deleted successfully');
      setDeleteDialog(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete category'));
    } finally {
      setDeletingCategory(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (deletingGroup) return; // Prevent double-click
    
    try {
      setDeletingGroup(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/deduction-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Group deleted successfully');
      setDeleteGroupDialog(false);
      setGroupToDelete(null);
      fetchGroups();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete group'));
    } finally {
      setDeletingGroup(false);
    }
  };

  const handleFreezeMonth = async (e) => {
    e.preventDefault();
    if (freezingMonth) return; // Prevent double-click
    if (!monthToFreeze) {
      toast.error('Please select a month');
      return;
    }
    try {
      setFreezingMonth(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/monthly-freezes/${monthToFreeze}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Month ${monthToFreeze} frozen`);
      setFreezeDialog(false);
      setMonthToFreeze('');
      fetchFrozenMonths();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to freeze month'));
    } finally {
      setFreezingMonth(false);
    }
  };

  const handleUnfreezeMonth = async (month) => {
    if (unfreezingMonth) return; // Prevent double-click
    if (!window.confirm(`Are you sure you want to unfreeze ${month}? This will allow edits to data from this month.`)) {
      return;
    }
    try {
      setUnfreezingMonth(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/monthly-freezes/${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Month ${month} unfrozen`);
      fetchFrozenMonths();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to unfreeze month'));
    } finally {
      setUnfreezingMonth(false);
    }
  };

  const openDeleteDialog = (categoryId, categoryName) => {
    setCategoryToDelete({ id: categoryId, name: categoryName });
    setDeleteDialog(true);
  };

  const openDeleteGroupDialog = (groupId, groupName) => {
    setGroupToDelete({ id: groupId, name: groupName });
    setDeleteGroupDialog(true);
  };

  // Handle drag end for categories within a group
  const handleCategoryDragEnd = async (event, groupName) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const groupCats = categories.filter(c => c.group === groupName);
    const oldIndex = groupCats.findIndex(c => c.id === active.id);
    const newIndex = groupCats.findIndex(c => c.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedGroupCats = arrayMove(groupCats, oldIndex, newIndex);
      
      // Update local state immediately for responsiveness
      const otherCats = categories.filter(c => c.group !== groupName);
      setCategories([...otherCats, ...reorderedGroupCats]);

      // Send reorder to backend
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/expense-categories/reorder`, 
          reorderedGroupCats.map(c => c.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        toast.error('Failed to save order');
        fetchCategories(); // Revert on error
      }
    }
  };

  // Handle drag end for groups
  const handleGroupDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = groups.findIndex(g => g.id === active.id);
    const newIndex = groups.findIndex(g => g.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedGroups = arrayMove(groups, oldIndex, newIndex);
      setGroups(reorderedGroups);

      // Send reorder to backend
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/deduction-groups/reorder`, 
          reorderedGroups.map(g => g.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        toast.error('Failed to save group order');
        fetchGroups(); // Revert on error
      }
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      partner: 'bg-rose-100 text-rose-800',
      general_manager: 'bg-pink-100 text-pink-800',
      accountant: 'bg-indigo-100 text-indigo-800',
      team_member: 'bg-teal-100 text-teal-800',
      cashier: 'bg-blue-100 text-blue-800',
      storekeeper: 'bg-green-100 text-green-800',
      bartender: 'bg-amber-100 text-amber-800',
      kitchen: 'bg-orange-100 text-orange-800',
      cafe: 'bg-cyan-100 text-cyan-800',
    };
    return <Badge className={colors[role] || 'bg-gray-100 text-gray-800'}>{role?.replace('_', ' ').toUpperCase()}</Badge>;
  };

  // Get unique group names from both groups collection and categories
  const allGroupNames = [...new Set([
    ...groups.map(g => g.name),
    ...categories.map(c => c.group)
  ])].filter(Boolean);

  const groupedCategories = categories.reduce((acc, cat) => {
    if (!acc[cat.group]) acc[cat.group] = [];
    acc[cat.group].push(cat);
    return acc;
  }, {});

  // Sort categories by sort_order within each group
  Object.keys(groupedCategories).forEach(group => {
    groupedCategories[group].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  });

  // Get sorted group names based on deduction_groups sort_order
  const sortedGroupNames = Object.keys(groupedCategories).sort((a, b) => {
    const groupA = groups.find(g => g.name === a);
    const groupB = groups.find(g => g.name === b);
    const orderA = groupA ? groupA.sort_order : 999;
    const orderB = groupB ? groupB.sort_order : 999;
    return orderA - orderB;
  });

  const getCategoryCountForGroup = (groupName) => {
    return categories.filter(c => c.group === groupName).length;
  };

  // ===== DATABASE BACKUP FUNCTIONS =====
  
  const handleCreateBackup = async () => {
    if (creatingBackup) return;
    
    if (!backupPassword || backupPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (backupPassword !== backupConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setCreatingBackup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/backup`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { password: backupPassword },
        responseType: 'blob'
      });
      
      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      link.setAttribute('download', `hoppers_backup_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup created and downloaded successfully');
      setBackupPassword('');
      setBackupConfirmPassword('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create backup'));
    } finally {
      setCreatingBackup(false);
    }
  };

  const fetchAutoBackups = async () => {
    setLoadingAutoBackups(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/auto-backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoBackups(response.data.backups || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to fetch auto-backups'));
    } finally {
      setLoadingAutoBackups(false);
    }
  };

  const handleImportBackup = async () => {
    if (importing) return;
    
    if (!importFile) {
      toast.error('Please select a backup file');
      return;
    }
    
    if (!importPassword) {
      toast.error('Please enter the backup password');
      return;
    }
    
    if (importConfirmPhrase !== 'REPLACE DATABASE') {
      toast.error('Please type exactly: REPLACE DATABASE');
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('password', importPassword);
      formData.append('confirmation_phrase', importConfirmPhrase);
      
      const response = await axios.post(`${API}/admin/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Database imported successfully! Auto-backup created: ${response.data.auto_backup_file}`);
      setImportDialog(false);
      setImportFile(null);
      setImportPassword('');
      setImportConfirmPhrase('');
      fetchAutoBackups();
      
      // Refresh data
      fetchCategories();
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to import backup'));
    } finally {
      setImporting(false);
    }
  };

  const handleRestoreAutoBackup = async () => {
    if (restoring) return;
    
    if (restoreConfirmPhrase !== 'RESTORE DATABASE') {
      toast.error('Please type exactly: RESTORE DATABASE');
      return;
    }
    
    setRestoring(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('filename', restoreDialog.filename);
      formData.append('confirmation_phrase', restoreConfirmPhrase);
      
      const response = await axios.post(`${API}/admin/restore-auto-backup`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Database restored from ${restoreDialog.filename}!`);
      setRestoreDialog({ open: false, filename: '' });
      setRestoreConfirmPhrase('');
      fetchAutoBackups();
      
      // Refresh data
      fetchCategories();
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to restore backup'));
    } finally {
      setRestoring(false);
    }
  };

  const handleDeleteAutoBackup = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/auto-backup/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Auto-backup deleted');
      fetchAutoBackups();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete backup'));
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <Tabs defaultValue="categories" className="w-full">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="setup" data-testid="setup-tab" className="flex items-center gap-1">
            <Settings2 className="w-3 h-3" />
            Setup
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="categories-tab">Categories</TabsTrigger>
          <TabsTrigger value="groups" data-testid="groups-tab">Groups</TabsTrigger>
          <TabsTrigger value="freeze" data-testid="freeze-tab">Freeze</TabsTrigger>
          <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
          <TabsTrigger value="access" data-testid="access-tab" className="flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Access
          </TabsTrigger>
          <TabsTrigger value="telegram" data-testid="telegram-tab" className="flex items-center gap-1">
            <Send className="w-3 h-3" />
            Telegram
          </TabsTrigger>
          <TabsTrigger value="backup" data-testid="backup-tab" className="flex items-center gap-1" onClick={fetchAutoBackups}>
            <FolderOpen className="w-3 h-3" />
            Backup
          </TabsTrigger>
          <TabsTrigger value="testdata" data-testid="testdata-tab" className="flex items-center gap-1">
            <FlaskConical className="w-3 h-3" />
            Test Data
          </TabsTrigger>
        </TabsList>

        {/* Initial Setup Tab */}
        <TabsContent value="setup" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5" />
                Initial Setup
              </CardTitle>
              <CardDescription>
                Set up opening cash and bank balances for the first day of operations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {initialSetupStatus.loading ? (
                <div className="text-center py-8">Loading setup status...</div>
              ) : (
                <>
                  {/* Setup Date */}
                  <div className="max-w-xs">
                    <Label htmlFor="setup-date">Setup Date</Label>
                    <Input
                      id="setup-date"
                      type="date"
                      value={setupDate}
                      onChange={(e) => setSetupDate(e.target.value)}
                      disabled={initialSetupStatus.cash}
                    />
                  </div>

                  {/* Opening Cash Setup */}
                  <div className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-blue-600" />
                        Opening Cash
                      </h3>
                      {initialSetupStatus.cash && (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" /> Configured
                        </Badge>
                      )}
                    </div>
                    {initialSetupStatus.cash ? (
                      <div className="text-sm text-gray-600">
                        Opening cash set to <strong>₹{initialSetupStatus.cashConfig?.opening_cash?.toLocaleString()}</strong> for {initialSetupStatus.cashConfig?.setup_date}
                      </div>
                    ) : (
                      <div className="flex items-end gap-3">
                        <div className="flex-1 max-w-xs">
                          <Label htmlFor="opening-cash">Opening Cash Amount (₹)</Label>
                          <Input
                            id="opening-cash"
                            type="number"
                            placeholder="Enter opening cash"
                            value={openingCash}
                            onChange={(e) => setOpeningCash(e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <Button onClick={handleSaveOpeningCash} disabled={savingSetup}>
                          {savingSetup ? 'Saving...' : 'Save Opening Cash'}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Bank Opening Balances */}
                  <div className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-blue-600" />
                        Bank Opening Balances
                      </h3>
                      {initialSetupStatus.bank && (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" /> Configured
                        </Badge>
                      )}
                    </div>
                    {initialSetupStatus.bank ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          {BANK_ACCOUNTS.map(account => (
                            <div key={account} className="flex justify-between items-center p-2 bg-white rounded border">
                              <div>
                                <span className="font-medium">{account}:</span>
                                <span className="ml-2">₹{(initialSetupStatus.bankBalances?.[account] || 0).toLocaleString()}</span>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-7 px-2 text-blue-600 hover:text-blue-800"
                                onClick={() => setEditBankDialog({ 
                                  open: true, 
                                  account, 
                                  amount: initialSetupStatus.bankBalances?.[account] || 0 
                                })}
                              >
                                Edit
                              </Button>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          Changes will reflect in all calculations from Jan 1st onwards.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-500">
                          Enter opening balance for each bank account. Leave empty or 0 for accounts with no balance.
                        </p>
                        <div className="grid grid-cols-3 gap-3">
                          {BANK_ACCOUNTS.map(account => (
                            <div key={account} className="flex items-center gap-2">
                              <Label className="w-20 text-sm font-medium">{account}</Label>
                              <Input
                                type="number"
                                placeholder="0.00"
                                value={bankBalances[account] || ''}
                                onChange={(e) => setBankBalances(prev => ({ ...prev, [account]: e.target.value }))}
                                min="0"
                                step="0.01"
                                className="flex-1"
                              />
                            </div>
                          ))}
                        </div>
                        <Button onClick={handleSaveBankBalances} disabled={savingSetup} className="mt-2">
                          {savingSetup ? 'Saving...' : 'Save Bank Balances'}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Setup Complete Message */}
                  {initialSetupStatus.cash && initialSetupStatus.bank && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-semibold">Initial setup complete!</span>
                      </div>
                      <p className="text-sm text-green-600 mt-1">
                        Cashiers can now start creating day closings from {setupDate}.
                      </p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Space Grotesk' }}>Deduction Categories</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Define categories for day closing deductions. Drag to reorder.</p>
              </div>
              <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-category-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Expense Category</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddCategory} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category Name</Label>
                      <Input
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                        placeholder="e.g., Card Payment, UPI, Bank Deposit"
                        required
                        data-testid="category-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Group</Label>
                      <select
                        className="w-full px-3 py-2 border rounded-md"
                        value={categoryForm.group}
                        onChange={(e) => setCategoryForm({ ...categoryForm, group: e.target.value })}
                        required
                        data-testid="category-group-select"
                      >
                        <option value="">Select a group</option>
                        {allGroupNames.map(group => (
                          <option key={group} value={group}>{group}</option>
                        ))}
                      </select>
                      {allGroupNames.length === 0 && (
                        <p className="text-sm text-amber-600">No groups available. Please create a group first.</p>
                      )}
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-category-btn" disabled={allGroupNames.length === 0}>
                      Add Category
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {Object.keys(groupedCategories).length === 0 ? (
                <p className="text-center py-8 text-gray-500">No categories defined yet</p>
              ) : (
                <div className="space-y-6">
                  {sortedGroupNames.map((group) => {
                    const cats = groupedCategories[group];
                    return (
                    <div key={group}>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide flex items-center gap-2">
                        <FolderOpen className="w-4 h-4" />
                        {group}
                        <Badge variant="outline" className="ml-2">{cats.length}</Badge>
                      </h3>
                      <DndContext 
                        sensors={sensors} 
                        collisionDetection={closestCenter} 
                        onDragEnd={(e) => handleCategoryDragEnd(e, group)}
                      >
                        <SortableContext items={cats.map(c => c.id)} strategy={verticalListSortingStrategy}>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {cats.map((cat) => (
                              <SortableCategoryItem 
                                key={cat.id} 
                                category={cat} 
                                onDelete={openDeleteDialog} 
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )})}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="groups" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Space Grotesk' }}>Deduction Groups</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Manage groups to organize expense categories</p>
              </div>
              <Dialog open={groupDialog} onOpenChange={setGroupDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-group-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Group
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Deduction Group</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddGroup} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Group Name</Label>
                      <Input
                        value={groupForm.name}
                        onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                        placeholder="e.g., Payment Modes, Discounts"
                        required
                        data-testid="group-name-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-group-btn">
                      Add Group
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {groups.length === 0 ? (
                <div className="text-center py-8">
                  <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No groups defined yet</p>
                  <p className="text-sm text-gray-400 mt-1">Create groups to organize your expense categories</p>
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
                  <SortableContext items={groups.map(g => g.id)} strategy={verticalListSortingStrategy}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {groups.map((group) => (
                        <SortableGroupItem 
                          key={group.id} 
                          group={group} 
                          onDelete={openDeleteGroupDialog}
                          categoryCount={getCategoryCountForGroup(group.name)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="freeze" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                  <Lock className="w-5 h-5" />
                  Monthly Auto-Freeze
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Frozen months prevent any edits to day closings, bank transactions, and bills from that period.
                  The system automatically freezes the previous month on the 1st of each new month.
                </p>
              </div>
              <Dialog open={freezeDialog} onOpenChange={setFreezeDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="freeze-month-btn">
                    <Lock className="w-4 h-4 mr-2" />
                    Freeze Month
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Freeze a Month</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleFreezeMonth} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Select Month</Label>
                      <Input
                        type="month"
                        value={monthToFreeze}
                        onChange={(e) => setMonthToFreeze(e.target.value)}
                        required
                        data-testid="freeze-month-input"
                      />
                    </div>
                    <p className="text-sm text-amber-600">
                      Warning: Freezing a month will prevent any edits to data from that period. Only admin can unfreeze.
                    </p>
                    <Button type="submit" className="w-full" data-testid="submit-freeze-btn">
                      Freeze Month
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {frozenMonths.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No months are currently frozen</p>
                  <p className="text-sm text-gray-400 mt-1">
                    The system will automatically freeze previous months on the 1st of each month
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead>Frozen At</TableHead>
                      <TableHead>Frozen By</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {frozenMonths.map((freeze) => (
                      <TableRow key={freeze.month}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-red-500" />
                            {new Date(freeze.month + '-01').toLocaleDateString('en-IN', { year: 'numeric', month: 'long' })}
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(freeze.frozen_at).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell>
                          <Badge variant={freeze.frozen_by === 'system' ? 'secondary' : 'default'}>
                            {freeze.frozen_by_name}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnfreezeMonth(freeze.month)}
                            className="text-amber-600 hover:text-amber-700"
                            data-testid={`unfreeze-${freeze.month}`}
                          >
                            <Unlock className="w-4 h-4 mr-1" />
                            Unfreeze
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <UserManagement 
            users={users} 
            currentUserId={user.id} 
            onRefresh={fetchUsers}
          />
        </TabsContent>

        <TabsContent value="access" className="space-y-4">
          <UserAccess />
        </TabsContent>

        {/* Telegram Settings Tab */}
        <TabsContent value="telegram" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-500" />
                Telegram Report Settings
              </CardTitle>
              <CardDescription>
                Configure automatic Cash Flow PDF reports via Telegram at 4 AM IST daily
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingTelegram ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Telegram Bot Status */}
                  <div className={`p-4 rounded-lg border ${telegramStatus.configured ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {telegramStatus.configured ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      )}
                      <span className={`font-medium ${telegramStatus.configured ? 'text-green-700' : 'text-yellow-700'}`}>
                        {telegramStatus.configured 
                          ? `Bot Connected: @${telegramStatus.bot_info?.username || 'Unknown'}` 
                          : 'Telegram Bot Not Configured'}
                      </span>
                    </div>
                    {!telegramStatus.configured && (
                      <p className="text-sm text-yellow-600">
                        Add TELEGRAM_BOT_TOKEN in backend/.env file. Get your token from @BotFather on Telegram.
                      </p>
                    )}
                  </div>

                  {/* Schedule Settings */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <Label>Daily Report Schedule</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Input
                          type="time"
                          value={telegramConfig.schedule_time}
                          onChange={(e) => setTelegramConfig(prev => ({ ...prev, schedule_time: e.target.value }))}
                          className="w-32"
                          data-testid="telegram-schedule-time"
                        />
                        <span className="text-sm text-gray-500">IST</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="telegram-enabled"
                            checked={telegramConfig.enabled}
                            onChange={(e) => setTelegramConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                            className="rounded"
                            data-testid="telegram-enabled-checkbox"
                          />
                          <Label htmlFor="telegram-enabled" className="text-sm">Enabled</Label>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">
                      Automatic reports will be sent for <strong>yesterday&apos;s</strong> data with zero-value sections hidden.
                    </p>
                  </div>

                  {/* Chat IDs */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-gray-500" />
                      <Label>Telegram Chat IDs (Recipients)</Label>
                    </div>
                    
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter Telegram Chat ID (e.g., 1234567890)"
                        value={newChatId}
                        onChange={(e) => setNewChatId(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addChatId()}
                        className="flex-1"
                        data-testid="telegram-new-chat-id"
                      />
                      <Button onClick={addChatId} variant="outline" data-testid="telegram-add-chat-id">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    
                    <p className="text-xs text-gray-500">
                      To get your Chat ID: Send a message to @{telegramStatus.bot_info?.username || 'your_bot'}, then visit 
                      <code className="mx-1 px-1 bg-gray-100 rounded">api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code>
                    </p>

                    {/* Chat IDs List */}
                    {telegramConfig.chat_ids.length > 0 ? (
                      <div className="border rounded-lg divide-y">
                        {telegramConfig.chat_ids.map((chatId, idx) => (
                          <div key={idx} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50">
                            <span className="font-mono text-sm">{chatId}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeChatId(chatId)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`telegram-remove-chat-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="border rounded-lg p-4 text-center text-gray-500 text-sm">
                        No Chat IDs configured. Add at least one to receive reports.
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      onClick={saveTelegramSettings}
                      disabled={savingTelegram}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="telegram-save-btn"
                    >
                      {savingTelegram ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Save Settings
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={testTelegramReport}
                      disabled={testingTelegram || telegramConfig.chat_ids.length === 0 || !telegramStatus.configured}
                      data-testid="telegram-test-btn"
                    >
                      {testingTelegram ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Test Report
                    </Button>
                    
                    <Button
                      variant="ghost"
                      onClick={fetchTelegramSettings}
                      data-testid="telegram-refresh-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Database Backup Tab */}
        <TabsContent value="backup" className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Create Backup Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-blue-500" />
                  Create Backup
                </CardTitle>
                <CardDescription>
                  Download encrypted backup of your entire database.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                  <p className="font-medium text-blue-800 mb-2">Backup includes:</p>
                  <ul className="list-disc list-inside text-blue-700 space-y-1">
                    <li>All vendors, bills, and payments</li>
                    <li>Day closings and deductions</li>
                    <li>Bank transactions</li>
                    <li>Employees and salaries</li>
                    <li>User accounts (passwords hashed)</li>
                  </ul>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="backup-password">Encryption Password</Label>
                  <Input
                    id="backup-password"
                    type="password"
                    placeholder="Min 6 characters"
                    value={backupPassword}
                    onChange={(e) => setBackupPassword(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="backup-confirm">Confirm Password</Label>
                  <Input
                    id="backup-confirm"
                    type="password"
                    placeholder="Confirm password"
                    value={backupConfirmPassword}
                    onChange={(e) => setBackupConfirmPassword(e.target.value)}
                  />
                </div>
                
                <Button 
                  onClick={handleCreateBackup} 
                  disabled={creatingBackup || !backupPassword || !backupConfirmPassword}
                  className="w-full"
                >
                  {creatingBackup ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Backup...
                    </>
                  ) : (
                    <>
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Download Backup
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Import Backup Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Import Backup
                </CardTitle>
                <CardDescription>
                  Replace current database with backup data.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
                  <p className="font-bold text-red-800 mb-2">Warning!</p>
                  <ul className="list-disc list-inside text-red-700 space-y-1">
                    <li>This will REPLACE all existing data</li>
                    <li>An auto-backup will be created first</li>
                    <li>Make sure you have the correct file</li>
                  </ul>
                </div>
                
                <Button 
                  variant="destructive" 
                  onClick={() => setImportDialog(true)}
                  className="w-full"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Import Database Backup
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Auto-Backups Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-green-500" />
                Server Auto-Backups (Safety Net)
              </CardTitle>
              <CardDescription>
                Auto-backups created before each import. Kept for 7 days. Use for emergency recovery.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAutoBackups ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                </div>
              ) : autoBackups.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No auto-backups found. They are created automatically before imports.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Filename</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {autoBackups.map((backup) => (
                      <TableRow key={backup.filename}>
                        <TableCell className="font-mono text-sm">{backup.filename}</TableCell>
                        <TableCell>{new Date(backup.created_at).toLocaleString()}</TableCell>
                        <TableCell>{backup.size_mb} MB</TableCell>
                        <TableCell className="text-sm text-gray-500">{backup.reason}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRestoreDialog({ open: true, filename: backup.filename })}
                          >
                            Restore
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteAutoBackup(backup.filename)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="testdata" className="space-y-4">
          <Card>
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                  <FlaskConical className="w-5 h-5 text-purple-500" />
                  Test Data Management
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Manage test data created during development and testing. Test data is excluded from all reports by default.
                </p>
              </div>
            </CardHeader>
            <CardContent>
              {/* Test Data Stats */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium mb-3">Test Data Summary</h3>
                {testDataStats ? (
                  <div>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="text-3xl font-bold text-purple-600">
                        {testDataStats.total_test_records}
                      </div>
                      <div className="text-sm text-gray-500">
                        Total test records<br />across all collections
                      </div>
                    </div>
                    
                    {Object.keys(testDataStats.collections || {}).length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {Object.entries(testDataStats.collections).map(([collection, count]) => (
                          <div key={collection} className="flex items-center justify-between p-2 bg-white rounded border">
                            <span className="text-sm truncate" title={collection}>
                              {collection.replace(/_/g, ' ')}
                            </span>
                            <Badge variant="secondary" className="ml-2">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-green-600 flex items-center gap-2">
                        ✅ No test data found. Your database is clean!
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Loading test data stats...</p>
                )}
              </div>

              {/* Info Box */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-medium text-blue-800 mb-2">ℹ️ How Test Mode Works</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Test data is created with <code className="bg-blue-100 px-1 rounded">is_test: true</code> flag</li>
                  <li>• All reports automatically exclude test data</li>
                  <li>• Test entries use names like &quot;TEST_&quot; prefix or year 2099</li>
                  <li>• Clearing test data removes ALL entries with test flags</li>
                </ul>
              </div>

              {/* Clear Button */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-100 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-red-800">Clear All Test Data</h4>
                    <p className="text-sm text-red-600 mt-1 mb-3">
                      This will permanently delete all test records from the database including test items, 
                      test vendors, test transactions, and test users. This action cannot be undone.
                    </p>
                    <Button 
                      variant="destructive" 
                      onClick={() => setClearTestDataDialog(true)}
                      disabled={!testDataStats || testDataStats.total_test_records === 0}
                      data-testid="clear-test-data-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear All Test Data
                    </Button>
                  </div>
                </div>
              </div>

              {/* Factory Reset Button */}
              <div className="p-4 bg-red-100 rounded-lg border-2 border-red-300">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-bold text-red-800 text-lg">⚠️ FACTORY RESET</h4>
                    <p className="text-sm text-red-700 mt-1 mb-3">
                      <strong>DANGER:</strong> This will permanently delete ALL data from the database - 
                      day closings, purchase bills, stock entries, price master, recipes, crockery, payroll, 
                      and everything else. Only user accounts will be preserved. Use this to start fresh.
                    </p>
                    <Button 
                      variant="destructive" 
                      onClick={() => setFactoryResetDialog(true)}
                      className="bg-red-700 hover:bg-red-800"
                      data-testid="factory-reset-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Factory Reset - Delete ALL Data
                    </Button>
                  </div>
                </div>
              </div>

              {/* Cache Management */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <RefreshCw className="w-6 h-6 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-800">Cache Management</h4>
                    <p className="text-sm text-blue-700 mt-1 mb-3">
                      If you&apos;re experiencing issues with outdated content after an update, 
                      clear the browser cache and reload the app.
                    </p>
                    <div className="flex items-center gap-3">
                      <Button 
                        variant="outline"
                        onClick={() => {
                          toast.info('Clearing cache and reloading...');
                          setTimeout(() => clearCacheAndReload(), 500);
                        }}
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                        data-testid="clear-cache-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Clear Cache & Reload
                      </Button>
                      <span className="text-xs text-blue-600">
                        Version: {versionService.getVersion().version}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Category Confirmation Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;<strong>{categoryToDelete?.name}</strong>&quot;? 
              This category will not be available for future deductions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteCategory(categoryToDelete?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Group Confirmation Dialog */}
      <AlertDialog open={deleteGroupDialog} onOpenChange={setDeleteGroupDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;<strong>{groupToDelete?.name}</strong>&quot;? 
              You can only delete groups that have no categories assigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteGroup(groupToDelete?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Test Data Confirmation Dialog */}
      <AlertDialog open={clearTestDataDialog} onOpenChange={setClearTestDataDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Clear All Test Data?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This will permanently delete:</p>
              <ul className="list-disc list-inside text-sm">
                <li>All entries with <code className="bg-gray-100 px-1 rounded">is_test: true</code></li>
                <li>Test items, vendors, and categories</li>
                <li>Test transactions and closings</li>
                <li>Test users (emails starting with test_ or containing _test@)</li>
              </ul>
              <p className="font-medium text-red-600 mt-2">This action cannot be undone!</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearingTestData}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearTestData}
              className="bg-red-600 hover:bg-red-700"
              disabled={clearingTestData}
            >
              {clearingTestData ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Test Data
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Factory Reset Confirmation Dialog */}
      <AlertDialog open={factoryResetDialog} onOpenChange={(open) => { setFactoryResetDialog(open); if (!open) setFactoryResetConfirm(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700 text-xl">
              <AlertTriangle className="w-6 h-6" />
              ⚠️ FACTORY RESET - ARE YOU SURE?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="font-bold text-red-600">This will permanently delete ALL data:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>All Day Closings & Bank Transactions</li>
                <li>All Purchase Bills & Vendor Bills</li>
                <li>All Stock Entries (Bar, Kitchen, Cafe)</li>
                <li>All Price Master & NA-Price Master Items</li>
                <li>All Recipes & Sale Items</li>
                <li>All Crockery Data</li>
                <li>All Store Ledger Entries</li>
                <li>All Payroll & Salary Records</li>
                <li>All Expense Categories & Groups</li>
                <li>All Audit Records</li>
              </ul>
              <p className="font-medium text-green-700 mt-2">✓ User accounts will be preserved</p>
              <div className="mt-4 p-3 bg-red-50 rounded border border-red-200">
                <p className="text-sm text-red-800 mb-2">Type <strong>RESET</strong> to confirm:</p>
                <Input 
                  value={factoryResetConfirm}
                  onChange={(e) => setFactoryResetConfirm(e.target.value)}
                  placeholder="Type RESET"
                  className="border-red-300"
                  data-testid="factory-reset-confirm-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={factoryResetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleFactoryReset}
              className="bg-red-700 hover:bg-red-800"
              disabled={factoryResetting || factoryResetConfirm !== 'RESET'}
            >
              {factoryResetting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ALL Data
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteUserDialog} onOpenChange={setDeleteUserDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete user &quot;<strong>{userToDelete?.name}</strong>&quot;? 
              This user will no longer be able to login to the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteUser(userToDelete?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog.open} onOpenChange={(open) => setPasswordDialog({ open, user: passwordDialog.user })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-500" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Changing password for: <strong>{passwordDialog.user?.name}</strong>
              </p>
              <p className="text-sm text-gray-500">{passwordDialog.user?.email}</p>
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 4 characters)"
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={!newPassword || newPassword.length < 4}>
              <Key className="w-4 h-4 mr-2" />
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Logs Dialog */}
      <Dialog open={logsDialog.open} onOpenChange={(open) => setLogsDialog({ ...logsDialog, open })}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-500" />
              User Activity Logs
            </DialogTitle>
            <p className="text-sm text-gray-500">View login sessions and activity history</p>
          </DialogHeader>
          
          {logsDialog.user && (
            <div className="mb-4 p-3 bg-slate-50 rounded-lg flex items-center justify-between">
              <div>
                <p className="font-medium">{logsDialog.user.name}</p>
                <p className="text-sm text-gray-500">{logsDialog.user.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Circle 
                  className={`w-3 h-3 ${logsDialog.user.is_online ? 'fill-green-500 text-green-500' : 'fill-gray-300 text-gray-300'}`} 
                />
                <span className={`text-sm ${logsDialog.user.is_online ? 'text-green-600' : 'text-gray-400'}`}>
                  {logsDialog.user.is_online ? 'Currently Online' : 'Offline'}
                </span>
              </div>
            </div>
          )}

          {loadingLogs ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <Tabs defaultValue="sessions" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="sessions">Login Sessions</TabsTrigger>
                <TabsTrigger value="activity">Activity Log</TabsTrigger>
              </TabsList>
              
              <TabsContent value="sessions" className="mt-4">
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Login Time</TableHead>
                        <TableHead>Logout Time</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Logout Type</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(!logsDialog.sessions || logsDialog.sessions.length === 0) ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                            No login sessions found
                          </TableCell>
                        </TableRow>
                      ) : (
                        logsDialog.sessions.map((session, idx) => (
                          <TableRow key={session?.id || idx}>
                            <TableCell className="text-sm">
                              {formatDateTime(session?.login_at)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {session?.logout_at ? formatDateTime(session.logout_at) : '-'}
                            </TableCell>
                            <TableCell className="text-sm">
                              {session?.duration_minutes ? `${session.duration_minutes} mins` : '-'}
                            </TableCell>
                            <TableCell>
                              {session?.is_active ? (
                                <span className="text-gray-400">-</span>
                              ) : session?.logout_reason === 'inactivity_timeout' ? (
                                <Badge className="bg-amber-100 text-amber-800">Auto (Inactivity)</Badge>
                              ) : session?.logout_reason === 'manual' ? (
                                <Badge className="bg-blue-100 text-blue-800">Manual</Badge>
                              ) : (
                                <Badge variant="secondary">Unknown</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {session?.is_active ? (
                                <Badge className="bg-green-100 text-green-800">Active</Badge>
                              ) : (
                                <Badge variant="secondary">Ended</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
              
              <TabsContent value="activity" className="mt-4">
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(!logsDialog.activities || logsDialog.activities.length === 0) ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                            No activity logs found
                          </TableCell>
                        </TableRow>
                      ) : (
                        logsDialog.activities.map((activity, idx) => (
                          <TableRow key={activity?.id || idx}>
                            <TableCell className="text-sm">
                              {formatDateTime(activity?.created_at)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                activity?.action === 'login' ? 'default' :
                                activity?.action === 'logout' ? 'secondary' :
                                activity?.action === 'auto_logout' ? 'outline' :
                                activity?.action === 'password_changed' ? 'destructive' :
                                'outline'
                              }
                              className={
                                activity?.action === 'auto_logout' ? 'bg-amber-100 text-amber-800 border-amber-200' : ''
                              }
                              >
                                {activity?.action === 'auto_logout' ? 'Auto Logout' : activity?.action || 'unknown'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {activity?.details || '-'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Bank Opening Balance Dialog */}
      <Dialog open={editBankDialog.open} onOpenChange={(open) => !open && setEditBankDialog({ open: false, account: '', amount: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Edit Opening Balance - {editBankDialog.account}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-bank-amount">Opening Balance Amount (₹)</Label>
              <Input
                id="edit-bank-amount"
                type="number"
                placeholder="Enter amount"
                value={editBankDialog.amount}
                onChange={(e) => setEditBankDialog(prev => ({ ...prev, amount: e.target.value }))}
                min="0"
                step="0.01"
                className="mt-1"
              />
            </div>
            <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
              Note: This change will affect all bank calculations from January 1st, 2026 onwards.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditBankDialog({ open: false, account: '', amount: '' })}>
              Cancel
            </Button>
            <Button onClick={handleEditBankBalance} disabled={savingBankEdit}>
              {savingBankEdit ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Database Dialog */}
      <AlertDialog open={importDialog} onOpenChange={setImportDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Import Database Backup
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-300 rounded-lg p-4 text-red-800">
                  <p className="font-bold mb-2">This action will:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Create an auto-backup of current data first</li>
                    <li>DELETE all existing data</li>
                    <li>REPLACE with data from backup file</li>
                  </ul>
                </div>
                
                <div className="space-y-2">
                  <Label>Select Backup File (.zip)</Label>
                  <Input
                    type="file"
                    accept=".zip"
                    onChange={(e) => setImportFile(e.target.files[0])}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Backup Password</Label>
                  <Input
                    type="password"
                    placeholder="Enter the password used during backup"
                    value={importPassword}
                    onChange={(e) => setImportPassword(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-red-600">Type REPLACE DATABASE to confirm</Label>
                  <Input
                    placeholder="REPLACE DATABASE"
                    value={importConfirmPhrase}
                    onChange={(e) => setImportConfirmPhrase(e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setImportFile(null);
              setImportPassword('');
              setImportConfirmPhrase('');
            }}>
              Cancel
            </AlertDialogCancel>
            <Button 
              variant="destructive" 
              onClick={handleImportBackup}
              disabled={importing || !importFile || !importPassword || importConfirmPhrase !== 'REPLACE DATABASE'}
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                'Import & Replace Database'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Auto-Backup Dialog */}
      <AlertDialog open={restoreDialog.open} onOpenChange={(open) => !open && setRestoreDialog({ open: false, filename: '' })}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <History className="w-5 h-5" />
              Restore from Auto-Backup
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-amber-800">
                  <p className="font-bold mb-2">Restoring from:</p>
                  <p className="font-mono text-sm bg-white p-2 rounded">{restoreDialog.filename}</p>
                  <p className="mt-2 text-sm">A safety backup will be created before restoring.</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-amber-600">Type RESTORE DATABASE to confirm</Label>
                  <Input
                    placeholder="RESTORE DATABASE"
                    value={restoreConfirmPhrase}
                    onChange={(e) => setRestoreConfirmPhrase(e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRestoreConfirmPhrase('')}>
              Cancel
            </AlertDialogCancel>
            <Button 
              variant="default"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleRestoreAutoBackup}
              disabled={restoring || restoreConfirmPhrase !== 'RESTORE DATABASE'}
            >
              {restoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                'Restore Database'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Settings;